// Conversations, newest activity first, with what each one says about quality: the user's rating, its open issues, the
// automatic signals, the model review's verdict. Filters live in the URL, so a view from the insights page can be linked.
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { bool, int, useQueryParams } from '../../hooks/useQueryParams';
import { dateTime, usd } from '../../services/format';
import { Alert, Badge, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table } from '../../components/ui';
import { CATEGORIES, OUTCOMES, OrgSelect, OutcomeBadge, RatingBadge, SEVERITIES, SeverityBadge, useLabel } from './common';

const PAGE_SIZE = 50;

export function ConversationsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const query = {
    days: int(get('days')) ?? 30,
    organization_id: int(get('organization_id')),
    user_id: int(get('user_id')),
    outcome: get('outcome') || undefined,
    severity: get('severity') || undefined,
    category: get('category') || undefined,
    rating: get('rating') || undefined,
    flagged: bool(get('flagged')),
    eval_candidate: bool(get('eval_candidate')),
    report: get('report') === 'any' ? ('any' as const) : undefined,
    issue: ['open', 'any', 'none'].includes(get('issue')) ? (get('issue') as 'open' | 'any' | 'none') : undefined,
    q: get('q') || undefined,
  };
  const key = JSON.stringify(query);
  const { data, error, loading } = useAsync(() => api.conversations({ ...query, page, page_size: PAGE_SIZE }), [key, page]);
  const outcome = useLabel('outcome');
  const severity = useLabel('severity');
  const category = useLabel('category');
  const signal = useLabel('signal');

  const yesNo = (name: string) => (
    <Select value={get(name)} onChange={(e) => set({ [name]: e.target.value })}>
      <option value="">{t('common.any')}</option>
      <option value="true">{t('common.yes')}</option>
      <option value="false">{t('common.no')}</option>
    </Select>
  );

  return (
    <div>
      <PageTitle title={t('quality.conversationsTitle')} subtitle={data ? t('common.count', { count: data.total }) : undefined} />
      <Filters onReset={reset}>
        <Field label={t('common.search')} className="w-56">
          <Input value={get('q')} onChange={(e) => set({ q: e.target.value })} placeholder={t('quality.searchHint')} />
        </Field>
        <Field label={t('quality.period')}>
          <Select value={String(query.days)} onChange={(e) => set({ days: e.target.value === '30' ? undefined : e.target.value })}>
            {[7, 30, 90, 365].map((d) => (
              <option key={d} value={d}>
                {t('quality.lastDays', { count: d })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('quality.organization')} className="w-48">
          <OrgSelect value={query.organization_id} onChange={(id) => set({ organization_id: id })} />
        </Field>
        <Field label={t('quality.flagged')}>{yesNo('flagged')}</Field>
        <Field label={t('quality.reports')}>
          <Select value={get('report')} onChange={(e) => set({ report: e.target.value })} data-testid="report-filter">
            <option value="">{t('common.any')}</option>
            <option value="any">{t('quality.reportFilter.any')}</option>
          </Select>
        </Field>
        <Field label={t('issues.title')}>
          <Select value={get('issue')} onChange={(e) => set({ issue: e.target.value })} data-testid="issue-filter">
            <option value="">{t('common.any')}</option>
            <option value="open">{t('issues.issueFilter.open')}</option>
            <option value="any">{t('issues.issueFilter.any')}</option>
            <option value="none">{t('issues.issueFilter.none')}</option>
          </Select>
        </Field>
        <Field label={t('quality.userRating')}>
          <Select value={get('rating')} onChange={(e) => set({ rating: e.target.value })}>
            <option value="">{t('common.any')}</option>
            <option value="up">{t('quality.rating.up')}</option>
            <option value="down">{t('quality.rating.down')}</option>
          </Select>
        </Field>
        <Field label={t('quality.outcomeLabel')}>
          <Select value={get('outcome')} onChange={(e) => set({ outcome: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {outcome(o)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('quality.severityLabel')}>
          <Select value={get('severity')} onChange={(e) => set({ severity: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {severity(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('quality.categoryLabel')}>
          <Select value={get('category')} onChange={(e) => set({ category: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {category(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('quality.evalCandidate')}>{yesNo('eval_candidate')}</Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table
        head={[t('quality.lastActivity'), t('quality.conversation'), t('quality.user'), t('quality.turns'), t('quality.userRating'), t('quality.signals'), t('quality.verdict'), t('quality.cost')]}
        empty={loading ? t('common.loading') : t('common.empty')}
      >
        {(data?.items ?? []).map((c) => (
          <Row key={c.thread_id} onClick={() => navigate(`/conversations/${c.thread_id}`)}>
            <Cell mono className="whitespace-nowrap">{dateTime(c.updated_at, i18n.language)}</Cell>
            <Cell className="max-w-sm">
              {c.restricted ? (
                <div className="truncate italic text-faint">{t('quality.restrictedRow')}</div>
              ) : (
                <div className="truncate text-ink" title={c.title}>{c.title || '—'}</div>
              )}
              {c.summary && <div className="truncate text-[11.5px] text-faint" title={c.summary}>{c.summary}</div>}
            </Cell>
            <Cell>
              <div className="font-mono text-[11.5px]">{c.user_email}</div>
              <div className="text-[11.5px] text-faint">{c.organization_name ?? '—'}</div>
            </Cell>
            <Cell mono>{c.turns}</Cell>
            <Cell>
              <div className="flex flex-wrap items-center gap-1">
                <RatingBadge rating={c.rating ?? (c.thumbs_down ? 'down' : null)} />
                {!!c.issues_open && <Badge tone="bad">{t('issues.openBadge', { count: c.issues_open })}</Badge>}
              </div>
            </Cell>
            <Cell>
              <div className="flex flex-wrap gap-1">
                {c.signals.map((s) => (
                  <Badge key={s.key} tone="warn">
                    {signal(s.key)}{s.count > 1 ? ` ×${s.count}` : ''}
                  </Badge>
                ))}
                {!c.signals.length && <span className="text-fainter">—</span>}
              </div>
            </Cell>
            <Cell>
              <div className="flex flex-wrap items-center gap-1">
                <OutcomeBadge outcome={c.outcome} />
                <SeverityBadge severity={c.severity} />
                {c.eval_candidate && <Badge tone="accent">{t('quality.eval')}</Badge>}
              </div>
            </Cell>
            <Cell mono>{usd(c.cost_usd, i18n.language)}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
